<template>
  <BaseModal
    v-if="activeMenu"
    is-open
    size="full"
    class-name="p-6 sm:p-8 room-menu-modal"
    wrapper-class-name="room-menu-modal-wrapper"
    close-on-esc-key-click
    mobile-full-screen-modal
    :title="activeMenu.name"
    @close="cancel"
  >
    <div class="w-full flex-1 min-h-0 overflow-hidden bg-grey-100">
      <object
        :data="previewUrl"
        type="application/pdf"
        :title="activeMenu.name"
        class="block w-full h-full"
      >
        <div class="h-full overflow-y-auto flex flex-col items-center p-4">
          <img
            v-if="hasFirstPageImage"
            :src="activeMenu.img"
            :alt="activeMenu.name"
            class="w-full max-w-2xl"
            @error="isPreviewImageLoadable = false"
          >
        </div>
      </object>
    </div>
  </BaseModal>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';

import BaseModal from '@/components/Ui/Modals/BaseModal.vue';
import { RoomMenu } from '@/modules/public/enquiry-form/enquiry-form.types';

/**
 * Chrome wraps an embedded PDF in its own toolbar and opens a page-thumbnail pane next to it by
 * default, which took a third of the preview away from the menu. `toolbar=1` collapses page sidebar
 * and enables the toolbar, which is a better compromise for a preview.
 */
const PDF_VIEWER_PARAMS = '#toolbar=1';

const activeMenu = ref<RoomMenu | null>(null);
const isPreviewImageLoadable = ref<boolean>(true);

const previewUrl = computed<string>(() => (activeMenu.value ? activeMenu.value.pdf + PDF_VIEWER_PARAMS : ''));

const hasFirstPageImage = computed<boolean>(() => !!activeMenu.value?.img && isPreviewImageLoadable.value);

const trackModalEvent = (element: string): void => {
  App.track.event('room_page_interaction', {
    section: 'menu_modal',
    element,
    element_value: activeMenu.value?.trackingElement ?? null,
    interaction: 'click',
  });
};

const close = (): void => {
  activeMenu.value = null;
};

const cancel = (): void => {
  trackModalEvent('cancel_button');
  close();
};

onMounted(() => {
  window.triggerShowRoomMenuModal = (menu: RoomMenu): void => {
    isPreviewImageLoadable.value = true;
    activeMenu.value = menu;
  };
});

onBeforeUnmount(() => {
  delete window.triggerShowRoomMenuModal;
});
</script>

<style lang="scss">
@use "@sass/base/variables";

/**
 * BaseModal top-aligns any modal taller than the viewport minus 160px and pads the wrapper by 80px
 * above and below, which pushed this nearly full-height modal below the fold. 24px matches the
 * 48px the modal height leaves free, so it fits the viewport without scrolling.
 */
.room-menu-modal-wrapper.ds-modal-wrapper--topAligned {
  @media #{variables.$only-for-large-min} {
    padding-top: 24px;
    padding-bottom: 24px;
  }
}

/**
 * Local to this modal: menus are mostly portrait pages, so the preview gets a narrow, nearly
 * full-height window. Capping the modal at the viewport and letting only .ds-modal-content grow
 * scrolls just the preview, not the whole sheet. The mobile 80px offset mirrors
 * $modal-wrapper-padding in design-system/_base-modal.scss.
 * Selector is deliberately specific enough to beat .ds-modal-wrapper--mobile-bottom .ds-modal.
 */
.ds-modal-wrapper .ds-modal.room-menu-modal {
  display: flex;
  flex-direction: column;
  height: calc(100dvh - 80px);
  overflow: hidden;

  @media #{variables.$only-for-large-min} {
    max-width: 800px;
    height: min(1200px, calc(100dvh - 48px));
  }

  > .ds-modal-header {
    flex-shrink: 0;
  }

  > .ds-modal-content {
    display: flex;
    flex-direction: column;
    flex: 1;
    min-height: 0;
  }
}
</style>
